import React, { useRef, useEffect, useState } from "react";
import { useGame, MODES } from "./store";

export default function LiveGraphs() {
  const canvasRef = useRef(null);
  const mode = useGame((s) => s.mode);
  const globalTimer = useGame((s) => s.globalTimer);
  const v = useGame((s) => s.v);
  const a = useGame((s) => s.a);
  const sProgress = useGame((s) => s.s);

  // Keep a history of data points: { t, v, a, d }
  const historyRef = useRef([]);

  useEffect(() => {
    if (mode !== MODES.ON_TRACK) return;
    
    // Add current data point
    const trackLen = 1600; // Approx track length
    const currentDist = sProgress * trackLen;
    historyRef.current.push({ t: globalTimer, v, a, d: currentDist });

    // Limit history to last 60 seconds (assuming 60fps, 3600 points max, but we sample every frame)
    // To prevent memory leak, keep max 2000 points
    if (historyRef.current.length > 2000) {
      historyRef.current.shift();
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;

    // Clear
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "rgba(10, 12, 24, 0.85)";
    ctx.fillRect(0, 0, width, height);

    // Draw Grids
    ctx.strokeStyle = "rgba(255,255,255,0.1)";
    ctx.lineWidth = 1;
    for (let i = 0; i < 10; i++) {
      const y = (i / 10) * height;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
      const x = (i / 10) * width;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
    }

    // We draw 3 distinct graphs stacked vertically
    const drawGraph = (yOffset, gHeight, color, title, dataKey, yMin, yMax) => {
      ctx.save();
      ctx.translate(0, yOffset);

      // Axis label
      ctx.fillStyle = color;
      ctx.font = "12px monospace";
      ctx.fillText(title, 10, 20);
      
      // Y max/min text
      ctx.fillStyle = "rgba(255,255,255,0.5)";
      ctx.fillText(yMax.toFixed(0), 10, 35);
      ctx.fillText(yMin.toFixed(0), 10, gHeight - 5);

      const hist = historyRef.current;
      if (hist.length < 2) { ctx.restore(); return; }

      // Map X from t0 to t0+60s, or just fit current history into width
      const tMin = Math.max(0, globalTimer - 30); // show last 30 seconds
      const tMax = tMin + 30;

      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();

      let lastX = 0;
      let lastY = 0;
      let currentVal = 0;

      for (let i = 0; i < hist.length; i++) {
        const pt = hist[i];
        if (pt.t < tMin) continue;
        const x = ((pt.t - tMin) / 30) * width;
        
        let val = pt[dataKey];
        currentVal = val;
        // clamp y
        val = Math.max(yMin, Math.min(yMax, val));
        const y = gHeight - ((val - yMin) / (yMax - yMin)) * gHeight;
        
        if (i === 0 || pt.t === tMin) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);

        lastX = x; lastY = y;
      }
      ctx.stroke();

      // Draw tracing dot
      ctx.beginPath();
      ctx.arc(lastX, lastY, 4, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Current value text next to dot
      ctx.fillStyle = "#fff";
      ctx.fillText(currentVal.toFixed(1), lastX + 8, lastY + 4);

      ctx.restore();
    };

    const section = height / 3;
    drawGraph(0, section, "#ff4d6d", "VELOCITY (m/s) vs TIME", "v", 0, 50);
    drawGraph(section, section, "#00ffaa", "DISTANCE (m) vs TIME", "d", 0, 1600);
    drawGraph(section * 2, section, "#ffd23f", "ACCEL (m/s²) vs TIME", "a", -20, 20);

  }, [v, a, sProgress, globalTimer, mode]);

  const isMobile = window.innerWidth < 768;
  const [showGraphs, setShowGraphs] = useState(false);

  if (mode !== MODES.ON_TRACK) return null;

  return (
    <>
      {/* Toggle button — always visible on mobile */}
      {isMobile && (
        <button
          onClick={() => setShowGraphs((s) => !s)}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 20,
            padding: "8px 14px",
            background: showGraphs ? "rgba(255,77,109,0.9)" : "rgba(0,255,170,0.15)",
            border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 10,
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            backdropFilter: "blur(8px)",
          }}
        >
          {showGraphs ? "Hide Graphs ✕" : "📈 Graphs"}
        </button>
      )}

      {/* Graph panel — always visible on desktop, toggled on mobile */}
      {(!isMobile || showGraphs) && (
        <div style={{
          position: "absolute",
          right: 16,
          top: isMobile ? 56 : 16,
          width: isMobile ? Math.min(window.innerWidth - 32, 300) : 320,
          height: isMobile ? 260 : 400,
          border: "2px solid rgba(255,255,255,0.1)",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          zIndex: 15,
          background: "rgba(10,12,24,0.95)",
        }}>
          <canvas
            ref={canvasRef}
            width={320}
            height={400}
            style={{ width: "100%", height: "100%", display: "block" }}
          />
        </div>
      )}
    </>
  );
}
